import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { idlePrefetchImages } from "@/utils/imagePreload";
import { PictureAsset } from "@/components/site/PictureAsset";
import "@/pages/ProjectGallery.css";

// Reusable non-case-study project page. Renders an editorial intro,
// then a quiet single-image gallery with minimal navigation. The
// gallery loops directly into the next project — no intermediate
// end page.
//
// Expects: {
//   name, type, location, description, imageBase, images: string[],
//   next: { slug, name, location }
// }

export default function ProjectGallery({ project }) {
  const { slug, name, type, location, description, credibility, imageBase, images, prev, next } =
    project;
  const total = images.length;
  const lastIndex = total - 1;
  const navigate = useNavigate();

  // Strip the extension from each filename so we can hand a bare
  // "base" (e.g. "/projects/tie-and-untie/01-foyer") to <PictureAsset />,
  // which appends "-1600.webp", "-3200.webp", "-1600.jpg", "-3200.jpg".
  // The images array in projectGalleries.js keeps the .jpg suffix for
  // human readability and so preload prefetch (below) can still fall
  // back to a plain URL when only the master exists.
  const stripExt = useCallback((src) => src.replace(/\.[^.]+$/, ""), []);
  const bases = useMemo(
    () => images.map((src) => `${imageBase}${stripExt(src)}`),
    [images, imageBase, stripExt],
  );

  const [index, setIndex] = useState(0);
  const figureRef = useRef(null);
  const imageRefs = useRef([]);

  // Hover overlay state — band height matches the displayed image
  // (not the figure box), and the custom cursor arrow follows the
  // pointer within that band.
  const [band, setBand] = useState({ top: 0, bottom: 0, ready: false });
  const [cursor, setCursor] = useState({
    active: false,
    x: 0,
    y: 0,
    side: "next",
  });

  useEffect(() => {
    document.title = `${name} — Ōraṇi`;
    window.scrollTo(0, 0);
    setIndex(0);
  }, [name]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : 0));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i < lastIndex) return i + 1;
      // On the last slide, advance directly to the next project.
      navigate(`/work/${next.slug}`);
      return i;
    });
  }, [lastIndex, navigate, next.slug]);

  // Measure the displayed image's vertical band inside the figure so
  // the hover overlay only activates over the image rows (not the
  // letterbox above/below). Re-measures whenever the active image,
  // viewport size, or the image's load state changes.
  useEffect(() => {
    const measure = () => {
      const fig = figureRef.current;
      const img = imageRefs.current[index];
      if (!fig || !img) return;
      const fr = fig.getBoundingClientRect();
      const ir = img.getBoundingClientRect();
      if (!ir.width || !ir.height) return;
      setBand({
        top: Math.max(0, ir.top - fr.top),
        bottom: Math.max(0, fr.bottom - ir.bottom),
        ready: true,
      });
    };

    measure();

    const img = imageRefs.current[index];
    let onLoad;
    if (img && !img.complete) {
      onLoad = () => measure();
      img.addEventListener("load", onLoad);
    }

    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      if (img && onLoad) img.removeEventListener("load", onLoad);
    };
  }, [index]);

  const handleOverlayMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const side = x < rect.width / 2 ? "prev" : "next";
    setCursor({ active: true, x, y, side });
  }, []);

  const handleOverlayLeave = useCallback(() => {
    setCursor((c) => ({ ...c, active: false }));
  }, []);

  const handleOverlayClick = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const left = e.clientX - rect.left < rect.width / 2;
      if (left) {
        if (index === 0) {
          if (prev) navigate(`/work/${prev.slug}`);
        } else {
          goPrev();
        }
      } else {
        if (index === lastIndex) {
          navigate(`/work/${next.slug}`);
        } else {
          goNext();
        }
      }
    },
    [index, lastIndex, prev, next.slug, navigate, goPrev, goNext],
  );

  // Keyboard navigation — only when not typing in an input/textarea.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (t && t.isContentEditable))
        return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  // Windowed mounting — only render <PictureAsset> for slides within
  // ±MOUNT_WINDOW of the active index. Everything else stays out of
  // the DOM entirely, so the browser cannot speculatively preload the
  // other slides' srcSet URLs (which would otherwise fetch all ~28
  // slides up-front despite the loading="lazy" attribute, because
  // every <img> is absolute-positioned inside the same figure box and
  // therefore all "in viewport" to the browser's lazy heuristic).
  //
  // The neighbouring in-window slides are still mounted, so the
  // opacity crossfade between the outgoing and incoming active slides
  // works exactly as before with no blank frame.
  const MOUNT_WINDOW = 3;
  const PREFETCH_LOOKAHEAD = 2;   // idle-prefetch slides just OUTSIDE the mount window

  // Idle-prefetch the next slides beyond the mount window so that when
  // the user advances quickly, the newly-mounted <PictureAsset> finds
  // its 1600w WebP already in the HTTP cache. We deliberately do NOT
  // synchronously `new Image()` the neighbours any more — the mounted
  // <PictureAsset>s in the window already trigger the browser's own
  // fetch, so a JS preload would only produce duplicate requests.
  useEffect(() => {
    const outer = [];
    for (let d = MOUNT_WINDOW + 1; d <= MOUNT_WINDOW + PREFETCH_LOOKAHEAD; d++) {
      const forward = index + d;
      const backward = index - d;
      if (forward <= lastIndex) outer.push(`${bases[forward]}-1600.webp`);
      if (backward >= 0) outer.push(`${bases[backward]}-1600.webp`);
    }
    idlePrefetchImages(outer);
  }, [index, bases, lastIndex]);

  // Pad the current index for display (01 / 29)
  const counter = useMemo(() => {
    const cur = String(index + 1).padStart(2, "0");
    const tot = String(total).padStart(2, "0");
    return `${cur} / ${tot}`;
  }, [index, total]);

  return (
    <main
      className="pg"
      data-testid={`project-gallery-${project.slug}`}
      aria-label={`${name} — visual study`}
    >
      <Link to="/work" className="pg__back" data-testid="pg-back">
        <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
          <path
            d="M21 5H1M5 1L1 5l4 4"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Selected Work
      </Link>

      <header className="pg__header" data-testid="pg-header">
        <span className="pg__eyebrow" data-testid="pg-eyebrow">
          {type}<span aria-hidden="true">,</span> {location}
        </span>
        <h1
          className="pg__title"
          data-testid="pg-title"
          data-slug={slug}
        >
          {name}
        </h1>
        {credibility ? (
          <p
            className="pg__credibility"
            data-testid="pg-credibility"
          >
            {credibility}
          </p>
        ) : null}
        <p className="pg__desc" data-testid="pg-desc">
          {description}
        </p>
      </header>

      {(
        <section
          className="pg__gallery"
          data-testid="pg-gallery"
          aria-label="Project gallery"
        >
          <figure
            ref={figureRef}
            className="pg__figure"
            data-testid="pg-figure"
          >
            {images.map((src, i) => {
              const inWindow = Math.abs(i - index) <= MOUNT_WINDOW;
              if (!inWindow) return null;
              return (
                <PictureAsset
                  key={src}
                  imgRef={(el) => (imageRefs.current[i] = el)}
                  base={bases[i]}
                  fallbackExt="jpg"
                  sizes="min(86vw, 1400px)"
                  alt={`${name} — image ${i + 1} of ${total}`}
                  className={[
                    "pg__image",
                    i === index ? "is-active" : "",
                  ].join(" ")}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={i === 0 ? "high" : "auto"}
                  aria-hidden={i === index ? "false" : "true"}
                  data-testid={`pg-image-${i}`}
                />
              );
            })}

            {/* Desktop hover overlay — a single band matching the
                displayed image height. Tracks the cursor and renders
                an editorial ‹ / › glyph that follows the pointer.
                Clicking left half navigates to the previous image
                (or to the previous project on the first slide);
                clicking right half navigates to the next image (or
                to the next project on the last slide). Hidden on
                touch / coarse-pointer devices via CSS. */}
            <div
              className="pg__hover"
              style={{ top: band.top, bottom: band.bottom }}
              data-side={cursor.side}
              data-active={cursor.active && band.ready ? "true" : "false"}
              data-edge={
                index === 0
                  ? "first"
                  : index === lastIndex
                  ? "last"
                  : "middle"
              }
              onMouseMove={handleOverlayMove}
              onMouseLeave={handleOverlayLeave}
              onClick={handleOverlayClick}
              aria-hidden="true"
              data-testid="pg-hover-overlay"
            >
              <span
                className="pg__hover-arrow"
                style={{ left: cursor.x, top: cursor.y }}
                aria-hidden="true"
                data-testid="pg-hover-arrow"
              >
                {cursor.side === "prev" ? "\u2039" : "\u203A"}
              </span>
            </div>
          </figure>

          <div className="pg__controls" data-testid="pg-controls">
            {index === 0 && prev ? (
              <Link
                to={`/work/${prev.slug}`}
                className="pg__ctrl pg__ctrl--prev pg__ctrl--prev-project"
                aria-label={`Previous project: ${prev.name}`}
                data-testid="pg-prev-project"
              >
                <span className="pg__ctrl-rule" aria-hidden="true" />
                {`Prev — ${prev.name}`}
              </Link>
            ) : (
              <button
                type="button"
                className="pg__ctrl pg__ctrl--prev"
                onClick={goPrev}
                disabled={index === 0}
                aria-label="Previous image"
                data-testid="pg-prev"
              >
                <span className="pg__ctrl-rule" aria-hidden="true" />
                Prev
              </button>
            )}

            <div className="pg__meta" data-testid="pg-meta">
              <span className="pg__counter" data-testid="pg-counter">
                {counter}
              </span>
              <span className="pg__sep" aria-hidden="true">
                —
              </span>
              <span className="pg__name" data-testid="pg-name">
                {name}
              </span>
            </div>

            {index === lastIndex ? (
              <Link
                to={`/work/${next.slug}`}
                className="pg__ctrl pg__ctrl--next pg__ctrl--next-project"
                aria-label={`Next project — ${next.name}`}
                data-testid="pg-next-project"
              >
                {`Next — ${next.name}`}
                <span className="pg__ctrl-rule" aria-hidden="true" />
              </Link>
            ) : (
              <button
                type="button"
                className="pg__ctrl pg__ctrl--next"
                onClick={goNext}
                aria-label="Next image"
                data-testid="pg-next"
              >
                Next
                <span className="pg__ctrl-rule" aria-hidden="true" />
              </button>
            )}
          </div>
        </section>
      )}
    </main>
  );
}
